import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createNotificationServiceClient, isTerminalSyntheticReminderState, type SyntheticReminderState } from "./client";

const POLL_INTERVAL_MS = 12_000;
// A one-shot synthetic reminder is due within 2 minutes and the worker runs every minute, but a
// reclaimed lease or a slow policy lookup can add a little more -- 6 minutes gives real margin
// before this diagnostic gives up, without polling indefinitely.
const POLL_TIMEOUT_MS = 6 * 60 * 1000;

export type SyntheticReminderValidationPhase = "idle" | "scheduling" | "active" | "timed-out" | "error";

// DEV-only diagnostic: schedules a one-shot synthetic reminder through the deployed reminder
// engine (see notification-service's POST/GET /v1/notifications/synthetic-reminders) and polls
// its status until it reaches a terminal state or this gives up. Never accepts a delay, title,
// body, or any ownership identifier -- see client.ts's scheduleSyntheticReminder.
export function useSyntheticReminderValidation() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.notificationService?.apiBaseUrl;

  const [phase, setPhase] = useState<SyntheticReminderValidationPhase>("idle");
  const [reminderState, setReminderState] = useState<SyntheticReminderState | null>(null);
  const [dueAt, setDueAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reminderIdRef = useRef<string | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const timeoutTimerRef = useRef<number | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }
    return createNotificationServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (timeoutTimerRef.current !== null) {
      window.clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  // Stop any in-flight polling on unmount -- this is a Settings-page diagnostic, not a
  // background task that should keep calling the API after the user navigates away.
  useEffect(() => stopPolling, [stopPolling]);

  const checkStatus = useCallback(async () => {
    const reminderId = reminderIdRef.current;
    if (!client || !reminderId) {
      return;
    }

    try {
      const result = await client.getSyntheticReminderStatus(reminderId);
      setDueAt(result.dueAt);
      setReminderState(result.state);
      if (isTerminalSyntheticReminderState(result.state)) {
        stopPolling();
      }
    } catch {
      // A transient status-check failure doesn't invalidate the diagnostic -- the next poll
      // tick (or the eventual timeout) resolves it either way, so this stays silent rather than
      // surfacing a scary error for what is likely just one dropped request.
    }
  }, [client, stopPolling]);

  const scheduleTestReminder = useCallback(async () => {
    if (!client) {
      setPhase("error");
      setError("Reminder validation is not available right now.");
      return;
    }

    stopPolling();
    setPhase("scheduling");
    setError(null);
    setReminderState(null);
    setDueAt(null);

    try {
      const result = await client.scheduleSyntheticReminder();
      reminderIdRef.current = result.reminderId;
      setDueAt(result.dueAt);
      setReminderState("scheduled");
      setPhase("active");

      pollTimerRef.current = window.setInterval(() => void checkStatus(), POLL_INTERVAL_MS);
      timeoutTimerRef.current = window.setTimeout(() => {
        stopPolling();
        setPhase("timed-out");
      }, POLL_TIMEOUT_MS);
    } catch (nextError) {
      setPhase("error");
      setError(nextError instanceof Error ? nextError.message : "We could not schedule a test reminder.");
    }
  }, [client, checkStatus, stopPolling]);

  const isBusy = phase === "scheduling" || (phase === "active" && reminderState !== null && !isTerminalSyntheticReminderState(reminderState));

  return {
    isAvailable: Boolean(client),
    phase,
    reminderState,
    dueAt,
    error,
    isBusy,
    scheduleTestReminder,
  };
}
